import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';
import { ChevronRight, ChevronDown, Compass, Sparkles, Zap } from 'lucide-react';
import api from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const DOMAIN_LABELS = {
  en: ['Physical Health', 'Strength', 'Energy', 'Space', 'Work', 'Money', 'Love', 'Friends', 'Emotional', 'Discipline', 'Learning', 'Meaning'],
  tr: ['Fiziksel Sağlık', 'Güç', 'Enerji', 'Alan', 'İş', 'Para', 'Aşk', 'Arkadaşlar', 'Duygusal', 'Disiplin', 'Öğrenme', 'Anlam'],
};

const DOMAIN_KEYS = [
  'physical_health', 'strength_fitness', 'energy', 'environment_order',
  'work_mission', 'money_financial', 'love_relationship', 'friends_community',
  'emotional_stability', 'discipline_consistency', 'learning_growth', 'meaning_purpose',
];

export const MapReveal = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [phase, setPhase] = useState(0);
  const [scores, setScores] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [solarScore, setSolarScore] = useState(0);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [assessmentRes, alignmentRes, meRes] = await Promise.all([
        api.get('/assessment/current'),
        api.get('/assessment/solar-alignment').catch(() => null),
        api.get('/auth/me').catch(() => null),
      ]);
      const data = assessmentRes.data;
      setScores(data.assessment.scores);
      setAnalysis(data.analysis);
      setSolarScore(alignmentRes?.data?.current?.solar_alignment?.score || 0);
      setUserName(meRes?.data?.name || '');
      setTimeout(() => setPhase(1), 500);
    } catch {
      navigate('/dashboard');
    }
  };

  useEffect(() => {
    if (phase >= 1 && solarScore > 0) {
      let current = 0;
      const step = Math.max(1, Math.floor(solarScore / 40));
      const interval = setInterval(() => {
        current += step;
        if (current >= solarScore) { current = solarScore; clearInterval(interval); }
        setAnimatedScore(current);
      }, 30);
      return () => clearInterval(interval);
    }
  }, [phase, solarScore]);

  useEffect(() => {
    if (phase === 1) {
      const t = setTimeout(() => setPhase(2), 2500);
      return () => clearTimeout(t);
    }
    if (phase === 2) {
      const t = setTimeout(() => setPhase(3), 3000);
      return () => clearTimeout(t);
    }
  }, [phase]);

  const labels = DOMAIN_LABELS[language] || DOMAIN_LABELS.en;
  const chartData = scores ? DOMAIN_KEYS.map((key, i) => ({
    domain: labels[i], current: scores[key] || 0, ideal: 10,
  })) : [];

  // Build a short AI-generated style summary from the data
  const buildSummary = () => {
    if (!scores || !analysis) return '';
    const sorted = DOMAIN_KEYS.map((k, i) => ({ key: k, label: labels[i], score: scores[k] || 0 })).sort((a, b) => b.score - a.score);
    const top = sorted.slice(0, 2).map(d => d.label).join(' & ');
    const low = sorted.slice(-2).map(d => d.label).join(' & ');
    const archs = analysis.archetypes?.length > 0 ? analysis.archetypes.join(' + ') : '';

    if (language === 'tr') {
      return `${archs ? archs + ' enerjisi aktif. ' : ''}En güçlü alanlarınız ${top}. Gelişim fırsatı: ${low}.`;
    }
    return `${archs ? archs + ' energy is active. ' : ''}Your strongest areas are ${top}. Growth edge: ${low}.`;
  };

  if (phase === 0 || !scores) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Compass className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <p className="text-[#6B7280] font-sans text-sm">
            {language === 'tr' ? 'Haritanız oluşturuluyor...' : 'Building your map...'}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative px-6 py-12">
      <div className="max-w-md mx-auto">

        {/* ===== ABOVE THE FOLD: Sun Map + Teaser Summary ===== */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[#6B7280] font-sans text-xs uppercase tracking-[0.3em] mb-1"
          >
            {userName ? `${userName},` : ''}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="font-serif text-3xl sm:text-4xl text-[#F8F5F2] mb-6"
          >
            {language === 'tr' ? 'Güneş Haritanız' : 'Your Sun Map'}
          </motion.h1>

          {/* Radar Chart */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="65%">
                <defs>
                  <radialGradient id="revealGrad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#B8860B" stopOpacity={0.3} />
                  </radialGradient>
                </defs>
                <PolarGrid stroke="rgba(255,140,66,0.12)" />
                <PolarAngleAxis dataKey="domain" tick={{ fill: '#D4AF37', fontSize: 9, fontFamily: 'Outfit' }} tickLine={false} />
                <Radar dataKey="ideal" stroke="#FF8C42" fill="#FF8C42" fillOpacity={0.06} strokeWidth={2} />
                <Radar dataKey="current" stroke="#D4AF37" fill="url(#revealGrad)" fillOpacity={0.6} strokeWidth={2} />
                <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" fill="#D4AF37" fontSize="34" fontFamily="Georgia, serif">{animatedScore}</text>
                <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" fill="#A1A1AA" fontSize="8" fontFamily="Outfit, sans-serif" letterSpacing="0.2em">SOLAR ALIGNMENT</text>
              </RadarChart>
            </ResponsiveContainer>
          </motion.div>
        </motion.div>

        {/* Phase 2: Teaser Summary */}
        {phase >= 2 && analysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mt-4 mb-6"
          >
            {/* Archetypes */}
            {analysis.archetypes?.length > 0 && (
              <div className="flex justify-center gap-2 mb-4">
                {analysis.archetypes.map((a, i) => (
                  <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-sans">{a}</span>
                ))}
              </div>
            )}

            {/* Short summary — distinct serif italic, different from the body text */}
            <p className="font-serif text-lg text-[#F8F5F2] italic leading-relaxed px-2" data-testid="reveal-teaser">
              {buildSummary()}
            </p>

            {/* Weekly focus as the anchor */}
            {analysis.weekly_focus && (
              <div className="mt-4 px-4 py-3 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/60 font-sans mb-1">
                  {language === 'tr' ? 'Haftalık Odağınız' : 'Your Weekly Focus'}
                </p>
                <p className="text-[#D4AF37] font-serif text-sm italic">{analysis.weekly_focus}</p>
              </div>
            )}

            {/* Scroll indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-6 flex flex-col items-center gap-1"
            >
              <p className="text-[#6B7280] font-sans text-xs">
                {language === 'tr' ? 'Tam okumanız için aşağı kaydırın' : 'Scroll down for your full reading'}
              </p>
              <ChevronDown className="w-4 h-4 text-[#6B7280] animate-bounce" />
            </motion.div>
          </motion.div>
        )}

        {/* ===== BELOW THE FOLD: Full Inner Guide Reading ===== */}
        {phase >= 2 && analysis && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
          >
            {/* Divider */}
            <div className="w-12 h-px bg-[#D4AF37]/30 mx-auto my-8" />

            {/* Full Analysis */}
            <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="reveal-inner-guide">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-sans">
                  {language === 'tr' ? 'İç Rehberiniz' : 'Your Inner Guide'}
                </span>
              </div>
              <p className="font-sans text-[#D1D1D6] leading-relaxed whitespace-pre-wrap text-sm">
                {analysis.analysis_text}
              </p>
            </div>

            {/* Priorities */}
            {analysis.priorities?.length > 0 && (
              <div className="mb-6" data-testid="reveal-priorities">
                <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans mb-3">
                  {language === 'tr' ? '30 Günlük Öncelikler' : '30-Day Priorities'}
                </p>
                <div className="space-y-2">
                  {analysis.priorities.map((p, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
                      <span className="text-[#D4AF37] mt-0.5">&#8226;</span>
                      <span className="text-[#D1D1D6] font-sans text-sm">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Habits */}
            {analysis.habits?.length > 0 && (
              <div className="mb-6" data-testid="reveal-habits">
                <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans mb-3">
                  {language === 'tr' ? 'Haftalık Alışkanlıklar' : 'Weekly Habits'}
                </p>
                <div className="space-y-2">
                  {analysis.habits.map((h, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
                      <Zap className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-[#D1D1D6] font-sans text-sm">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Navigator Set */}
            <p className="text-center text-[#D4AF37] font-serif text-sm italic mb-6">
              {language === 'tr' ? 'Navigatörünüz ayarlandı.' : 'Your Navigator has been set.'}
            </p>
          </motion.div>
        )}

        {/* ===== CTA: Start Your Inner Navigation ===== */}
        {phase >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-4 mb-12"
          >
            <button
              onClick={() => {
                localStorage.setItem('in_map_revealed', 'true');
                localStorage.setItem('in_show_tour', 'true');
                navigate('/dashboard');
              }}
              className="w-full py-4 rounded-2xl font-sans text-sm uppercase tracking-widest transition-all duration-300 bg-[#D4AF37] text-[#030305] hover:bg-[#FFBF00] flex items-center justify-center gap-2"
              data-testid="map-reveal-cta"
            >
              {language === 'tr' ? 'İç Navigasyonunu Başlat' : 'Start Your Inner Navigation'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MapReveal;
